import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import {
  IFetchOrderNormalizedResponse,
  IOrderListProps,
} from "../../../interfaces/Index";
import searchicon from "../../../assets/images/no-search-icon.svg";
import close from "../../../assets/images/close.svg";
import { _ } from "../../../utils/lib";
import ButtonComponent from "../../common/Button/ButtonComponent";
import DataTable from "../../common/DataTable/DataTable";
import {
  headerData,
  footerData,
  bodyDataAndKeyTypes,
} from "./OrderTableContants";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import { Can } from "../../../config/Can";
import "./OrderList.scss";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchOrders: any
) => {
  if (val.length >= 1) {
    pageScrollPosition.current = 0;
    fetchOrders({
      criteria: val,
    });
  }
};

const OrderList: React.FunctionComponent<IOrderListProps> = (
  props: IOrderListProps
) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [ordersData, setData] = useState<Array<IFetchOrderNormalizedResponse>>(
    []
  );
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const { fetchOrders, orders, isOrderAddedorUpdated } = props;
    if (orders.length === 0 || isOrderAddedorUpdated) {
      fetchOrders();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { orders, pagination } = props;
    if (orders) {
      setData(orders);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.orders]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isOrderCreated || props.isOrderUpdated) {
      const message = props.isOrderCreated
        ? "Order created successfully!"
        : "Order updated successfully";
      handleToaster(false, message);
    }
  }, [props.isOrderCreated, props.isOrderUpdated]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) => doAPIsearch(value, pageScrollPosition, props.fetchOrders),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchOrders } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchOrders();
    }
  };

  const editOrder = (e: any, i: number) => {
    const { addUpdatePageHandler } = props;

    addUpdatePageHandler(true, ordersData[i], searchTerm, window.pageYOffset);
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchOrders } = props;
    fetchOrders();
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    const { fetchOrders, pagination } = props;
    if (pagination) {
      pageScrollPosition.current = window.pageYOffset;
      setLoadMoreText({
        text: "Loading...",
        isLoading: true,
      });
      if (isSearch) {
        fetchOrders({
          page: pagination,
          criteria: searchTerm,
        });
      } else {
        fetchOrders({
          page: pagination,
        });
      }
    }
  };

  return (
    <div className="order-list-container">
      {props.isLoading && <LoaderComponent />}
      {/* <Modal
        modalHeading="Add a batch of categories"
        showModal={props.isCategoryBulkUploading || props.isCategoryBulkUploaded}
        modalLoading={props.isCategoryBulkUploading}
        isSuccess={props.isCategoryBulkUploaded}
      ></Modal> */}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-9">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src="../../../assets/images/close.svg" alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by order # and phone number"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={close} alt="close" />
              </div>
            </InputGroup>
          </section>
          <section className="col-3">
            <Can I="create" a="order">
              <ButtonComponent
                buttonText="New order"
                onClickHandler={() =>
                  props.addUpdatePageHandler(
                    false,
                    null,
                    searchTerm,
                    window.pageYOffset
                  )
                }
                buttonClass="btn-primary btn btn-secondary button-large"
              />
            </Can>
          </section>
        </section>
      </section>
      <Can I="read" a="order">
        <DataTable
          tableId="order-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={ordersData}
          noSearchResultFlag={isSearch}
          editRowHandler={editOrder}
        />
        {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

export default OrderList;
